import Checkbox from '@/design_system/Checkbox';
import Stack from '@/design_system/Stack';
import {
  ArticleConditionForm,
  ArticleConditionFormType,
  useUpdateArticleCondition,
} from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useDatabaseTranslation } from '@/services/i18n';
import useViewPort from '@/utils/useViewport';

import { ConditionFormQuestion, useQuestionErrors } from './ConditionQuestion';

export const ConditionQuestionCheckbox = ({
  question,
  parentQuestion,
  type,
  editableArticleConditionForm,
}: {
  question: Extract<ConditionFormQuestion, { type: 'checkbox' }>;
  parentQuestion?: ConditionFormQuestion;
  type: ArticleConditionFormType;
  editableArticleConditionForm?: ArticleConditionForm;
}) => {
  const { isMobile } = useViewPort();
  const { _db } = useDatabaseTranslation();
  const { article, view, initialConditionFormData, qualityControlFormData } = useArticleContext();

  const conditionForm =
    type === 'initial-condition'
      ? initialConditionFormData?.conditionForm
      : qualityControlFormData?.conditionForm;

  const errors = useQuestionErrors({ question, parentQuestion, type });
  const { mutateAsync: updateArticleCondition, isPending: isUpdateArticleCondition } =
    useUpdateArticleCondition({
      requestId: article.requestId,
      articleId: article.id,
      conditionForm,
    });

  const editable =
    !!editableArticleConditionForm &&
    (type === 'initial-condition' ? view.initialCondition.editable : view.qualityControl.editable);

  const selectedAnswerIds =
    editableArticleConditionForm?.articleConditions
      ?.filter((articleCondition) => articleCondition.questionId === question.id)
      ?.map((articleCondition) => articleCondition.answerId) ?? [];

  const handleChange = ({ answerId, isSelected }: { answerId: string; isSelected: boolean }) => {
    const newValues = isSelected
      ? [...selectedAnswerIds, answerId]
      : selectedAnswerIds.filter((value) => value !== answerId);

    updateArticleCondition({
      questionId: question.id,
      answers: newValues.map((id) => ({ id })),
    });
  };

  if (!editable) {
    return null;
  }

  return (
    <Stack gap="0.5rem" ariaLabel={_db(question.title)}>
      <span className="label-100-medium">{_db(question.title)}</span>
      <Stack row={!isMobile} gap={isMobile ? '0.5rem' : '1rem'} style={{ rowGap: '0.5rem' }}>
        {question.answers?.map((answer) => {
          const label = _db(answer.text);

          return (
            <Checkbox
              key={answer.id}
              value={answer.id}
              onChange={(isSelected) =>
                handleChange({
                  answerId: answer.id,
                  isSelected,
                })
              }
              isSelected={selectedAnswerIds.includes(answer.id)}
              isInvalid={errors?.noAnswers}
              isDisabled={isUpdateArticleCondition}
            >
              {label}
            </Checkbox>
          );
        })}
      </Stack>
    </Stack>
  );
};
